import { Component, OnInit, Renderer2, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { GridComponent, PageChangeEvent } from '@progress/kendo-angular-grid';
import { filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AccountType, GLAcctDataModel } from '../../interfaces/general-ledger';
import { GeneralLedgerService } from '../../services/general-ledger.service';

const createFormGroup = (dataItem) =>
    new UntypedFormGroup({
        accountNumber: new UntypedFormControl(dataItem.accountNumber, Validators.required),
        accountName: new UntypedFormControl(dataItem.accountName),
        accountType: new UntypedFormControl(dataItem.accountType),
        accountCategory: new UntypedFormControl(dataItem.accountCategory),
        isLoanAccount: new UntypedFormControl(dataItem.isLoanAccount),
    });

const matches = (el, selector) => (el.matches || el.msMatchesSelector).call(el, selector);

@Component({
    selector: 'app-general-ledger-mapping',
    templateUrl: './general-ledger-mapping.component.html',
    styleUrls: ['./general-ledger-mapping.component.scss'],
})
export class GeneralLedgerMappingComponent implements OnInit {
    @ViewChild(GridComponent)
    private grid: GridComponent;
    public formGroup: UntypedFormGroup;
    private docClickSubscription: any;
    private editedRowIndex: number;

    noAccts: boolean;
    noDataType: NoDataTypes;
    glAccts: { data: any; total: any };
    glAcctData: GLAcctDataModel[];
    filteredAccts: GLAcctDataModel[];
    exportData: GLAcctDataModel[];
    addNew: boolean = false;
    exportFileName: string;
    glAcctSearchTerm: string;
    // TODO: Move the bellow account types to a uidb table;
    // Until then please compare the account type values in the ui sproc GLAcctUpsert
    accountTypes: AccountType[] = [
        { typeKey: 0, type: 'Asset' },
        { typeKey: 1, type: 'Liability' },
        { typeKey: 2, type: 'Revenue' },
        { typeKey: 3, type: 'Expense' },
    ];
    suggestionMap: Set<string> = new Set();
    suggestions: string[] = [];
    filteredSuggestions: Observable<string[]>;
    reqNameSubject: Subject<string> = new Subject<string>();
    glAcctSort: SortDescriptor[];
    loading: boolean;
    pageSize = 10;
    skip = 0;

    constructor(
        private generalLedgerService: GeneralLedgerService,
        private reusableFunctions: ReusableFunctions,
        private toast: ToastService,
        private renderer: Renderer2
    ) {
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }

    ngOnInit() {
        this.exportData = [];
        this.glAcctSort = [];
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'General Ledger Mapping',
            inputSource: NoDataSourceType.listOfEntities,
        };
        this.getData();
        this.docClickSubscription = this.renderer.listen(
            'document',
            'click',
            this.onDocumentClick.bind(this)
        );
        this.filteredSuggestions = this.reqNameSubject.pipe(
            startWith(''),
            map((name) => (name ? this._filter(name) : this.suggestions.slice()))
        );
    }

    ngOnDestroy(): void {
        this.docClickSubscription();
    }

    getData() {
        this.loading = true;
        this.generalLedgerService.getGLAccts().subscribe(
            (data) => {
                if (data && data.length > 0) {
                    this.glAcctData = data;
                    for (var obj of this.glAcctData) {
                        obj.accountTypeKey = this.accountTypes.findIndex(
                            (val) => val.type == obj.accountType
                        );
                        this.suggestionMap.add(obj.accountCategory);
                    }
                    this.suggestions = Array.from(this.suggestionMap.values());
                } else {
                    this.noAccts = true;
                    this.glAcctData = [];
                    this.suggestions = [];
                }
                this.updateGrid();
                this.loading = false;
            },
            (error) => {
                this.loading = false;
                this.noDataType.action = NoDataActionType.error;
                this.glAcctData = [];
                throw new Error(error);
            }
        );
    }

    updateGrid() {
        let orderedData = orderBy(
            this.filteredAccts ? this.filteredAccts : this.glAcctData,
            this.glAcctSort
        );
        this.exportData = orderedData.map((obj) => ({ ...obj }));
        this.glAccts = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    pageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.updateGrid();
    }

    addNewAccount() {
        this.addNew = true;
        this.formGroup = createFormGroup({
            accountNumber: '',
            accountName: '',
            accountType: '',
            accountCategory: '',
            isLoanAccount: null,
        });

        this.grid.addRow(this.formGroup);
    }

    cancelNewAccount() {
        this.closeEditor();
        this.addNew = false;
    }

    getTypeKey(accountType: string) {
        return this.accountTypes.find((type) => type.type == accountType);
    }

    submitChanges() {
        this.generalLedgerService.submitChanges(this.glAcctData).subscribe(
            (data) => {
                this.toast.success('General ledger mapping changes saved.');
            },
            (error) => {
                this.toast.warning('Error saving general ledger mapping changes.');
                throw new Error(error);
            }
        );
    }

    insertGLAcct(data: GLAcctDataModel) {
        this.generalLedgerService.insertGLAcct(data).subscribe(
            (data) => {
                this.toast.success('General ledger mapping changes saved.');
                this.getData();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    filterAccts() {
        this.filteredAccts = filterBy(this.glAcctData, {
            logic: 'or',
            filters: [
                {
                    field: 'accountNumber',
                    operator: 'contains',
                    value: this.glAcctSearchTerm,
                    ignoreCase: true,
                },
                {
                    field: 'accountName',
                    operator: 'contains',
                    value: this.glAcctSearchTerm,
                    ignoreCase: true,
                },
                {
                    field: 'accountType',
                    operator: 'contains',
                    value: this.glAcctSearchTerm,
                    ignoreCase: true,
                },
                {
                    field: 'accountCategory',
                    operator: 'contains',
                    value: this.glAcctSearchTerm,
                    ignoreCase: true,
                },
            ],
        });
        let orderedData = orderBy(this.filteredAccts, this.glAcctSort);
        this.glAccts = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    sortChange(sort: SortDescriptor[]): void {
        this.glAcctSort = sort;
        this.updateGrid();
    }

    reqNameChanged(value: string) {
        this.reqNameSubject.next(value);
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this.reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'general-ledger-mappings_' + dateTime + '_.xlsx';
        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }

    public cellClickHandler({ rowIndex }): void {
        this.editedRowIndex = rowIndex;
    }

    private closeEditor(): void {
        this.grid.closeRow(this.editedRowIndex);
        this.editedRowIndex = undefined;
        this.formGroup = undefined;
    }

    private onDocumentClick(e: any): void {
        if (
            this.formGroup &&
            this.formGroup.valid &&
            (matches(e.target, '#saveNewAccount') ||
                matches(e.target, '#saveNewAccount > span.mat-button-wrapper'))
        ) {
            this.saveCurrent();
        }
    }

    private saveCurrent(): void {
        if (this.formGroup) {
            this.insertGLAcct(this.formGroup.value);
            this.closeEditor();
            this.addNew = false;
        }
    }

    private _filter(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.suggestions.filter((option) => option.toLowerCase().includes(filterValue));
    }
}
